// Broker Channel Batch (WS5) — the soft checklist behind a stage click on
// the BrokerStageStepper. One stage-readiness GET drives auto-evaluated
// rows for the target transition; rows are INFORMATIONAL ONLY (met = gold
// check, unmet = muted circle, never red) and NEVER block the move — the
// lifecycle stays fully manual. A backward move shows a reassurance note
// instead of a checklist. The footer posts the existing status-change
// endpoint (Brokers.Qualify, optional reason).

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, Info } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import {
  Button,
  Label,
  SubmitButton,
  Textarea,
  useSubmittingMutation,
  useToast,
} from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import { brokerKeys } from "@/hooks/brokers/brokerKeys";
import {
  brokersApi,
  PIPELINE_STATUSES,
  type BrokerStageReadinessDto,
  type BrokerStatus,
} from "@/api/brokers";

export interface ChecklistRow {
  label: string;
  /** undefined = static info row (bullet, no met/unmet state). */
  met?: boolean;
}

/** Builds the approved soft-checklist content for a forward transition. */
export function buildStageChecklist(
  target: BrokerStatus,
  r: BrokerStageReadinessDto,
): ChecklistRow[] {
  // Escalation PR-2 (F4) — a broker Phoenix met as a Buyer CRM listing agent is
  // not on the recruitment ladder at all. The server already empties
  // certificationItems for them, but eoOnFile / eoCurrent / licenseOnFile /
  // trainingCompleted are computed straight off Broker columns and do NOT go
  // through the catalog, so without this the panel would still paint four unmet
  // rows for someone nobody is recruiting. Same shape as the isJunior rows below.
  const skipRecruitmentLadder = r.isJunior || r.isListingAgentSourced === true;

  const certItem = (code: string) =>
    r.certificationItems.find((c) => c.code === code);
  const certVerifiedAndFiled = (code: string) => {
    const item = certItem(code);
    // A waived item has no filed report — the waiver itself satisfies the row.
    return (
      !!item &&
      ((item.status === "Verified" && item.reportFiled) || item.status === "Waived")
    );
  };
  const gates = r.stageGates
    .filter((g) => g.stageTo === target)
    .map((g) => ({ label: g.label, met: g.certified }));

  switch (target) {
    case "Qualified":
      return [
        { label: "Application approved", met: r.applicationApproved },
        { label: "Contact details on file", met: r.hasContactDetails },
        { label: "State/territory identified", met: r.hasState },
        ...gates,
        { label: "Introductory call completed and notes logged" },
      ];
    case "Onboarding":
      return [
        { label: "Application approved", met: r.applicationApproved },
        { label: "Portal access granted", met: r.portalAccessGranted },
        { label: "Liaison assigned", met: r.liaisonAssigned },
        // Onboarding differentiation — a Junior broker carries no own E&O
        // (the brokerage covers them), so the advisory E&O row is suppressed.
        ...(skipRecruitmentLadder
          ? []
          : [{ label: "E&O insurance on file", met: r.eoOnFile && r.eoCurrent }]),
        ...(r.isListingAgentSourced === true
          ? []
          : [{ label: "License details on file", met: r.licenseOnFile }]),
        ...gates,
        { label: "Onboarding welcome materials sent" },
      ];
    case "Certified":
      return [
        {
          label: "Internal training completed",
          met:
            r.trainingCompleted ||
            certItem("BRK-TRN")?.status === "Verified" ||
            certItem("BRK-TRN")?.status === "Waived",
        },
        // Onboarding differentiation — a Junior broker has no BRK-INS / E&O
        // requirement (covered by the brokerage), so both E&O advisory rows are
        // suppressed. The license rows still apply.
        ...(skipRecruitmentLadder
          ? []
          : [
              {
                label: "Insurance certification verified & report filed",
                met: certVerifiedAndFiled("BRK-INS"),
              },
            ]),
        ...(r.isListingAgentSourced === true
          ? []
          : [
              {
                label: "License certification verified & report filed",
                met: certVerifiedAndFiled("BRK-LIC"),
              },
            ]),
        ...(skipRecruitmentLadder
          ? []
          : [{ label: "E&O insurance current", met: r.eoCurrent }]),
        ...gates,
        { label: "Certification review completed with Broker Manager (C0)" },
      ];
    default:
      // →Prospect is always a backward move; no checklist content exists.
      return [];
  }
}

export function StageAdvancePanel({
  broker,
  target,
  onClose,
}: {
  broker: { id: string; companyName: string; name?: string | null; status: BrokerStatus };
  target: BrokerStatus;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState("");

  const currentIndex = PIPELINE_STATUSES.indexOf(broker.status);
  const targetIndex = PIPELINE_STATUSES.indexOf(target);
  const backward = targetIndex < currentIndex;

  const readiness = useQuery({
    queryKey: ["broker-stage-readiness", broker.id],
    queryFn: () => brokersApi.stageReadiness(broker.id),
    enabled: !backward,
  });

  const move = useSubmittingMutation(
    () => brokersApi.changeStatus(broker.id, target, reason.trim() || undefined),
    {
      onSuccess: () => {
        toast.success(`Moved to ${target}.`);
        void qc.invalidateQueries({ queryKey: brokerKeys.all });
        void qc.invalidateQueries({ queryKey: ["broker-stage-readiness", broker.id] });
        void qc.invalidateQueries({ queryKey: ["broker-transitions", broker.id] });
        onClose();
      },
      onError: (e) => toast.error(extractErrorMessage(e)),
    },
  );

  const displayName = broker.name || broker.companyName;
  const rows = readiness.data ? buildStageChecklist(target, readiness.data) : [];

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Move to ${target}`}
      description={`${broker.companyName} — currently ${broker.status}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={move.pending}>
            Cancel
          </Button>
          {/* The checklist is advisory — this button is never disabled by
              checklist state, only while the POST is in flight. */}
          <SubmitButton
            pending={move.pending}
            pendingLabel="Moving…"
            onClick={() => void move.submit()}
            data-testid="stage-advance-confirm"
          >
            Move to {target}
          </SubmitButton>
        </>
      }
    >
      {backward ? (
        <p className="text-small text-gray-dark" data-testid="stage-backward-note">
          Moving {displayName} back to {target}. This doesn&apos;t undo any
          recorded approvals or certifications.
        </p>
      ) : (
        <>
          <p className="text-caption text-gray-mid mb-space-2">
            Readiness Checklist:
          </p>
          {readiness.isLoading && (
            <div className="text-caption text-gray-mid">Loading readiness…</div>
          )}
          {readiness.isError && (
            <div className="text-caption text-gray-mid italic">
              Readiness could not be loaded — you can still move the broker.
            </div>
          )}
          {readiness.data && (
            <ul className="m-0 list-none p-0 space-y-space-2" data-testid="stage-checklist">
              {rows.map((row) => (
                <li key={row.label} className="flex items-start gap-space-2 text-small">
                  {row.met === undefined ? (
                    <Info size={14} className="mt-[2px] shrink-0 text-status-info" aria-hidden />
                  ) : row.met ? (
                    <CheckCircle2
                      size={14}
                      className="mt-[2px] shrink-0 text-gold-shadow"
                      aria-hidden
                      data-checklist-state="met"
                    />
                  ) : (
                    <Circle
                      size={14}
                      className="mt-[2px] shrink-0 text-gray-mid"
                      aria-hidden
                      data-checklist-state="unmet"
                    />
                  )}
                  <span className={row.met === false ? "text-gray-mid" : undefined}>
                    {row.label}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <div className="mt-space-3">
        <Label className="mb-space-1 block">Reason / notes (optional)</Label>
        <Textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Optional note for the activity timeline."
        />
      </div>
    </Modal>
  );
}
